import { CompanyUserSummary } from '@treasury/domain/company/user';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/jh-ui';
import { RadioOption } from '@treasury/presentation/components/jha-wc';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { FiUser } from '@treasury/domain/users/fi-user.entity';
import '@treasury/presentation/components/jha-wc/tm-radio-button';

export enum ResetPasswordType {
    Email = 'email',
    Manual = 'manual',
}

export type ResetPasswordForm = {
    changePasswordType: ResetPasswordType;
    temporaryPassword?: string;
};
const options: RadioOption[] = [
    { value: ResetPasswordType.Email, label: 'Email Password Link' },
    { value: ResetPasswordType.Manual, label: 'Manually Set Password' },
];
const tagName = 'tm-bo-reset-password';
@customElement(tagName)
export class ResetPasswordComponent extends TmBaseComponent {
    @property({ type: Object })
    public user?: CompanyUserSummary | FiUser;

    @state()
    private changePasswordType: ResetPasswordType = ResetPasswordType.Email;

    @state()
    private temporaryPassword: string = '';

    private get userName(): string {
        if (this.user === undefined) {
            return '';
        }

        return this.user.userName ?? '';
    }

    public render() {
        return html`
            <div class="flex flex-col gap-6 max-w-[400px]">
                <div class="flex flex-col gap-8 pt-6">
                    <tm-radio-button
                        name="changePasswordType"
                        class="flex flex-col gap-4"
                        .initial=${this.changePasswordType}
                        @change=${(e: CustomEvent) => this.handleTypeChange(e)}
                        .options=${options}
                    >
                    </tm-radio-button>
                    ${this.changePasswordType === ResetPasswordType.Manual
                        ? html`<tm-input
                              type="password"
                              label="Temporary Password:"
                              name="temporaryPassword"
                              .initial=${this.temporaryPassword}
                              required
                              showIndicator
                              @input=${(e: InputEvent) => this.handleTempPasswordChange(e)}
                          ></tm-input>`
                        : nothing}
                </div>
                ${this.changePasswordType === ResetPasswordType.Email
                    ? html`<p class="text-base font-medium">
                          An email will be sent to ${this.userName} with a link to reset their
                          password.
                      </p>`
                    : html`<p class="text-base font-medium">
                          An automated notification will not be sent. You'll need to manually share
                          the temporary password with the user. They'll be prompted to create a new
                          password upon their first successful login.
                      </p>`}
            </div>
        `;
    }

    private handleTypeChange(e: CustomEvent) {
        this.changePasswordType = e.detail.value as ResetPasswordType;
        this.dispatchEvent(
            new CustomEvent<string>('change-password-type', {
                detail: this.changePasswordType,
            })
        );
    }

    private handleTempPasswordChange(e: InputEvent) {
        if (!e.target) {
            return;
        }
        const target = e.target as HTMLInputElement;
        this.temporaryPassword = target.value;

        this.dispatchEvent(
            new CustomEvent<string>('temporary-password-change', {
                detail: this.temporaryPassword,
            })
        );
    }

    public static get styles() {
        return css`
            :host {
                --radio-label-font-weight: 700;
            }
        `;
    }
}
